import {provideAppInitializer, inject} from '@angular/core';
import {Router} from '@angular/router';
import {OAuthService, AuthConfig} from 'angular-oauth2-oidc';
import {filter} from 'rxjs/operators';
import {environment} from './env';
import {UserService} from './services/user/user.service';
import {AppLoggerService} from "./core/logging";

function isPublicPath(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return [
    '/login',
    '/invitation/preview',
    '/error/forbidden',
    '/',
  ].includes(path);
}

function getOidcConfig() {
  const oidc = (environment as any)?.oidc;
  if (!oidc) {
    // Fail fast with a clear message instead of "cannot access property 'issuer'"
    throw new Error('[auth] Missing environment.oidc configuration. Check angular.json fileReplacements and environment.*.ts files.');
  }
  return oidc;
}

const oidc = getOidcConfig();

export const authConfig: AuthConfig = {
  issuer: oidc.issuer,
  clientId: oidc.clientId,
  redirectUri: oidc.redirectUri,
  // Optional fields (provide sane defaults)
  postLogoutRedirectUri: oidc.postLogoutRedirectUri ?? window.location.origin,
  silentRefreshRedirectUri: oidc.silentRefreshRedirectUri,
  scope: oidc.scope,
  requireHttps: oidc.requireHttps ?? true,
  responseType: oidc.responseType,
  sessionChecksEnabled: oidc.sessionChecksEnabled,
  showDebugInformation: !environment.production,
};

const KEY_RETURN = 'app:returnUrl';
const KEY_PROGRESS = 'app:loginInProgress';

function readReturnState(state?: string | null) {
  // State is passed via initCodeFlow(state). If you do not encode, do not decode here.
  return state ?? null;
}

function consumeLoginContext(state?: string | null) {
  const inProgress = sessionStorage.getItem(KEY_PROGRESS) === '1';
  const fromState = readReturnState(state);
  const fromStorage = sessionStorage.getItem(KEY_RETURN);

  // Always cleanup
  sessionStorage.removeItem(KEY_RETURN);
  sessionStorage.removeItem(KEY_PROGRESS);

  return {inProgress, finalTarget: fromState || fromStorage};
}

/**
 * Provides an Angular 20 compatible app initializer.
 * Important: inject() must run inside the initializer function (DI context),
 * not while creating the provider.
 */
export function provideAuthInit() {
  return provideAppInitializer(async () => {
    // DI context is available here
    const oauth = inject(OAuthService);
    const appLoggerService = inject(AppLoggerService);
    const router = inject(Router);
    const userService = inject(UserService);

    const log = appLoggerService.create('AuthInit');

    const currentUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;

    const onLoginPage = window.location.pathname === '/login';

    oauth.configure(authConfig);
    oauth.setupAutomaticSilentRefresh();

    // 1) Try to login (reads tokens from URL callback and/or from storage)
    await oauth.loadDiscoveryDocumentAndTryLogin({
      onTokenReceived: ({state}) => {
        const {inProgress, finalTarget} = consumeLoginContext(state);

        log.info('token received', {state, inProgress, finalTarget});

        // Only redirect automatically if this was an interactive login flow
        if (inProgress && finalTarget?.startsWith('/')) {
          router.navigateByUrl(finalTarget, {replaceUrl: true});
        } else if (onLoginPage) {
          // Optional: never stay on /login once you have a token
          router.navigateByUrl('/home', {replaceUrl: true});
        }
      },
    });

    // 2) Fallback: user already had a valid token, so onTokenReceived might not fire
    if (oauth.hasValidAccessToken()) {
      const inProgress = sessionStorage.getItem(KEY_PROGRESS) === '1';
      const fromStorage = sessionStorage.getItem(KEY_RETURN);

      log.info('already logged in at init', {inProgress, fromStorage, onLoginPage});

      // If we are on /login, we usually want to leave it (and also drop ?iss=...)
      if (onLoginPage && (inProgress || fromStorage)) {
        const finalTarget = fromStorage || '/home';

        sessionStorage.removeItem(KEY_RETURN);
        sessionStorage.removeItem(KEY_PROGRESS);

        if (finalTarget.startsWith('/')) {
          router.navigateByUrl(finalTarget, {replaceUrl: true});
        }
      }
    }

    // 3) If no token and current route is protected, start interactive login
    if (!oauth.hasValidAccessToken()) {
      if (!isPublicPath(currentUrl)) {
        sessionStorage.setItem(KEY_RETURN, currentUrl);
        sessionStorage.setItem(KEY_PROGRESS, '1');

        // Do NOT encode state manually; the library will handle it
        oauth.initCodeFlow(currentUrl);
      }
      return;
    }

    // 4) Debug event logging (useful during integration)
    oauth.events.subscribe(e => log.debug('oauth/oidc event', {
      type: e.type,
    }));

    // 5) Load user profile if logged in
    const loadUser = () => {
      if (!oauth.hasValidAccessToken()) return;
      userService.getUserProfile().subscribe({
        next: (u: any) => log.debug('User profile loaded', u),
        error: (err: any) => log.error('User profile failed', err),
      });
    };

    loadUser();

    // 6) Reload user profile on token events
    oauth.events
      .pipe(filter(e => e.type === 'token_received'))
      .subscribe(() => loadUser());

    // 7) Handle refresh problems / session termination
    oauth.events
      .pipe(filter(e => e.type === 'token_refresh_error' || e.type === 'session_terminated'))
      .subscribe(() => {
        log.warn('Session ended or refresh failed — logging out');
        oauth.logOut();
      });

    // 8) Optional: refresh right before expiry (depends on your flow/provider)
    oauth.events
      .pipe(filter(e => e.type === 'token_expires'))
      .subscribe(() => {
        oauth.refreshToken().catch(() => {
        });
      });
  });
}
